import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import '../shared/MessagingActions.css';
import FilterBar from '@company/components/CommunicationHub/FilterBar/FilterBar';
import DirectMessagesTable from '@company/components/CommunicationHub/CommunicationHubTables/DirectMessagesTable';
import {
  getDirectMessages,
  markAllDirectChatsAsRead,
} from '@company/services/com-hub/api';
import ThPagination from '@components/ThPagination/ThPagination';
import ComHubSkeletonLoader from '@company/components/CommunicationHub/ComHubSkeletonLoader';
import {
  ComHubQueryParamsEnum,
  DirectMessage,
  ChatModeEnum,
} from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';
import {
  handleComHubSearch,
  extractComHubUrlParams,
  handleMarkMessagesAsRead,
} from '@utils/ComHubUtils';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import { useDirectMessageContext } from '@company/state/directMessageContext';

interface DirectMessagesProps {
  onShowWorkerProfile: (workerId: number) => void;
  onShowAlert?: (success: boolean, message: string) => void;
  onCounterUpdate: (delta: number) => void;
  onResetCounter?: () => void;
}

function DirectMessages({
  onShowWorkerProfile,
  onShowAlert,
  onCounterUpdate,
  onResetCounter,
}: DirectMessagesProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchState, setSearchState] = useState<{
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }>();
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [chatMode, setChatMode] = useState<ChatModeEnum | null>(
    ChatModeEnum.INBOX
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const queryClient = useQueryClient();
  const { markAsRead, markAsUnread } = useDirectMessageContext();

  // CONSTANTS FOR QUERY
  const queryKeyDirectMessages = [
    QueryKeys.ComHub.DIRECT_CHAT,
    currentOffset,
    showOnlyNew,
    chatMode,
    searchState,
  ];

  const {
    data: directMessagesResponse,
    isLoading,
    isFetching,
    error: directMessagesError,
  } = useQuery({
    queryKey: queryKeyDirectMessages,
    queryFn: () =>
      getDirectMessages(
        currentOffset,
        DEFAULT_ITEMS_PER_PAGE,
        searchState?.searchText,
        searchState?.dateRange,
        showOnlyNew,
        chatMode
      ),
    enabled: isInitialized,
  });

  useEffect(() => {
    if (directMessagesError) {
      onShowAlert?.(false, directMessagesError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directMessagesError]);

  // Initialize state from URL parameters
  useEffect(() => {
    if (isInitialized) return;

    const {
      searchState: urlSearchState,
      showOnlyNew: urlShowOnlyNew,
      offset: urlOffset,
    } = extractComHubUrlParams(searchParams);

    if (urlSearchState) {
      setSearchState(urlSearchState);
    }
    setShowOnlyNew(!!urlShowOnlyNew);
    setCurrentOffset(Number(urlOffset) || 0);
    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  const { mutate: markAllDirectChats, isPending: isMarkingAll } = useMutation({
    mutationFn: async () => {
      const response = await markAllDirectChatsAsRead();

      if (!response?.success) {
        throw new Error(
          response?.message ||
            response?.error ||
            'Failed to mark messages as read.'
        );
      }

      return response;
    },
    onSuccess: async (response) => {
      onShowAlert?.(
        true,
        response.message || 'All direct messages marked as read.'
      );

      await queryClient.invalidateQueries({
        queryKey: queryKeyDirectMessages,
      });
      onResetCounter?.();
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to mark messages as read.';
      onShowAlert?.(false, message);
    },
  });

  const handleMarkAllAsRead = useCallback(() => {
    if (isMarkingAll || isFetching) {
      return;
    }

    markAllDirectChats();
  }, [isMarkingAll, isFetching, markAllDirectChats]);

  const unreadDirectMessages = useMemo<DirectMessage[]>(() => {
    if (!directMessagesResponse?.directChats) {
      return [];
    }

    return directMessagesResponse.directChats.filter(
      (directMessage) => directMessage.unreadMessages > 0
    );
  }, [directMessagesResponse]);

  const unreadDirectMessagesCount = unreadDirectMessages.length;

  const readDirectChats = useMemo<DirectMessage[]>(() => {
    if (!directMessagesResponse?.directChats) {
      return [];
    }

    return directMessagesResponse.directChats.filter(
      (chat) => chat.unreadMessages === 0 && chat.hasMultipleAuthors
    );
  }, [directMessagesResponse]);

  const handleMarkAllAsUnread = useCallback(() => {
    if (isMarkingAll || isFetching || !readDirectChats.length) {
      return;
    }

    readDirectChats.forEach((chat) => {
      markAsUnread(chat.chatId, () => onCounterUpdate(1));
    });
  }, [
    isMarkingAll,
    isFetching,
    markAsUnread,
    readDirectChats,
    onCounterUpdate,
  ]);

  const handleMarkUnreadChats = useCallback(() => {
    if (isMarkingAll || isFetching || !unreadDirectMessages.length) {
      return;
    }

    const count = unreadDirectMessages.length;
    handleMarkMessagesAsRead(
      unreadDirectMessages,
      markAsRead,
      markAsUnread,
      (message) => message.chatId,
      (message) => message.unreadMessages > 0,
      () => onCounterUpdate(-count)
    );
  }, [
    isMarkingAll,
    isFetching,
    markAsRead,
    markAsUnread,
    unreadDirectMessages,
    onCounterUpdate,
  ]);

  const handleOnSearch = useCallback(
    (search: {
      searchText: string;
      dateRange: { startDate: Date | undefined; endDate: Date | undefined };
    }) => {
      handleComHubSearch(search, setSearchState, setSearchParams);
      setCurrentOffset(0);
    },
    [setSearchParams]
  );

  // Update URL when offset changes
  useEffect(() => {
    if (!isInitialized) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(ComHubQueryParamsEnum.OFFSET, currentOffset.toString());
    newSearchParams.set(
      ComHubQueryParamsEnum.MAX,
      DEFAULT_ITEMS_PER_PAGE.toString()
    );
    setSearchParams(newSearchParams);
  }, [currentOffset, searchParams, setSearchParams, isInitialized]);

  return (
    <>
      <FilterBar
        onSearch={handleOnSearch}
        showOnlyNew={showOnlyNew}
        onShowOnlyNew={() => {
          setShowOnlyNew((prevState) => !prevState);
          setCurrentOffset(0);
        }}
        isMessagingTab
        chatMode={chatMode}
        onChatModeChange={(mode) => {
          setChatMode(mode);
          setCurrentOffset(0);
        }}
        onMarkAllAsRead={handleMarkAllAsRead}
        onMarkAllAsUnread={
          readDirectChats.length ? handleMarkAllAsUnread : undefined
        }
        markAllAsUnreadLabel={`Mark ${readDirectChats.length} unread`}
        onMarkUnreadMessages={handleMarkUnreadChats}
        markUnreadMessagesLabel={`Mark ${unreadDirectMessagesCount} read`}
      />
      {isLoading && <ComHubSkeletonLoader />}
      {directMessagesResponse ? (
        <>
          {directMessagesResponse.directChats ? (
            <DirectMessagesTable
              directMessages={directMessagesResponse.directChats}
              showHeader={false}
              onShowWorkerProfile={onShowWorkerProfile}
              onCounterUpdate={onCounterUpdate}
            />
          ) : (
            <p>No new notifications.</p>
          )}

          <ThPagination
            currentOffset={directMessagesResponse.pagination?.offset}
            onPageChange={setCurrentOffset}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
            totalItems={directMessagesResponse.pagination?.count}
          />
        </>
      ) : (
        <p className="mt-5 d-flex justify-content-center">
          {!isLoading && 'There are no direct messages.'}
        </p>
      )}
    </>
  );
}

export default DirectMessages;
